import { Suspense, useEffect, useMemo, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { UserRoundCheck, UserRoundPlus, CircleDollarSign } from 'lucide-react';
import { ApplicantTargetPopover } from '@/components/pages/edit-targets/applicant-target-popover';
import { BudgetTargetPopover } from '@/components/pages/edit-targets/budget-target-popover';
import { CurrencyStats } from '@/components/ui/currency-stats';
import { HireStats } from '@/components/ui/hire-stats';
import { MetricGroup, MetricGroupItem } from '@/components/ui/metric-group';
import { Skeleton } from '@/components/ui/skeleton';
import { ConversionStats } from '@/components/ui/conversion-stats';
import { useBudgetTargetSignal } from '@/contexts/budget-target-signal-context';
import {
  useOpeningAnalytics,
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useShowHiringGoalEmptyState } from '@/components/pages/channels/use-show-hiring-goal-empty-state';
import {
  getGoalWindow,
  tooltipInstant,
} from '@/components/pages/opening-details/overview/hiring-goal-view/goal-window';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { formatShortDate } from '@/utils/date-util';

function AnalyticsContent() {
  const intl = useIntl();
  const { metricsViewMode } = useOpeningContext();
  const { opening, hiringGoal, filterDuration } = useOpeningDetails();
  const { analyticsData } = useOpeningAnalytics();
  const showHiringGoalEmptyState = useShowHiringGoalEmptyState(hiringGoal);
  const { budgetTargetOpenSignal } = useBudgetTargetSignal();
  const spendCardRef = useRef<HTMLDivElement>(null);
  const localDays = useLocalDateRanges();

  // The goal's start and end at the opening, for the hires tooltip. A goal with an
  // end date but no end_at keeps the date-only copy.
  const goalWindow = useMemo(
    () =>
      localDays &&
      metricsViewMode === 'hiring_goal' &&
      hiringGoal?.external_id &&
      (hiringGoal.end_at || !hiringGoal.hire_by)
        ? getGoalWindow(
            hiringGoal.start_date,
            hiringGoal.end_at,
            opening.timeZone,
          )
        : null,
    [localDays, metricsViewMode, hiringGoal, opening.timeZone],
  );

  // Targets are only editable from the hiring-goal view, and only when the
  // opening has an active hiring goal to attach targets to.
  const isReadOnly =
    !hiringGoal?.is_current || metricsViewMode !== 'hiring_goal';

  const {
    applicants_stats: applicants,
    hire_stats: hires,
    cpa_stats: cpa,
    budget_stats: budget,
    cost_per_hire: cph,
    conversion_stats: conversion,
    spend_stats: spendStats,
    target_budget: targetBudget,
  } = analyticsData;

  useEffect(() => {
    if (budgetTargetOpenSignal === 0) return;
    spendCardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [budgetTargetOpenSignal]);

  const hideExpectedStats = !hiringGoal?.external_id;

  // Header label for the "Sourced this period" section — shown on the right side
  // of the header next to the info icon.
  //
  // In hiring-goal mode the window is always the goal's full duration
  // (start_date → hire_by). In date-range mode it comes from filterDuration,
  // which may be a rolling N-day window or an explicit start/end pair.
  const sourcedHeaderLabel = (() => {
    if (metricsViewMode === 'hiring_goal' && hiringGoal) {
      return intl.formatMessage(
        {
          defaultMessage: 'Applicants sourced {start} – {end}',
          description:
            'Label showing the date range during which applicants were sourced, spanning a hiring goal from {start} to {end}',
        },
        {
          start: formatShortDate(hiringGoal.start_date),
          end: hiringGoal.hire_by
            ? formatShortDate(hiringGoal.hire_by)
            : intl.formatMessage({
                defaultMessage: 'Today',
                description: "Today's date",
              }),
        },
      );
    }
    const numericPeriod = Number(filterDuration.metrics_period);
    if (isFinite(numericPeriod) && numericPeriod > 0) {
      return intl.formatMessage(
        {
          defaultMessage: 'Applicants sourced in last {days} days',
          description:
            'Label showing applicants were counted over a rolling window of the last {days} days',
        },
        { days: numericPeriod },
      );
    }
    if (filterDuration.start_date) {
      return intl.formatMessage(
        {
          defaultMessage: 'Applicants sourced {start} – {end}',
          description:
            'Label showing applicants were counted between a specific {start} and {end} date',
        },
        {
          start: formatShortDate(filterDuration.start_date),
          end: formatShortDate(
            filterDuration.end_date ?? new Date().toISOString(),
          ),
        },
      );
    }
    return undefined;
  })();

  // Shorter date-range string used inside tooltip body copy — same priority
  // as sourcedHeaderLabel but in a compact form suitable for inline text.
  const dateRangeLabel = (() => {
    if (metricsViewMode === 'hiring_goal' && hiringGoal) {
      return `${formatShortDate(hiringGoal.start_date)} – ${formatShortDate(hiringGoal.hire_by)}`;
    }
    const numericPeriod = Number(filterDuration.metrics_period);
    if (isFinite(numericPeriod) && numericPeriod > 0) {
      return intl.formatMessage(
        {
          defaultMessage: 'last {days} days',
          description:
            'Compact phrase for a rolling date range of the past {days} days, used as an inline date reference',
        },
        { days: numericPeriod },
      );
    }
    if (filterDuration.start_date) {
      return `${formatShortDate(filterDuration.start_date)} – ${formatShortDate(filterDuration.end_date ?? new Date().toISOString())}`;
    }
    return undefined;
  })();

  if (showHiringGoalEmptyState) return null;

  return (
    <div className="shrink-0 overflow-x-auto">
      <div className="flex min-w-max flex-row items-stretch gap-4 pb-2">
        {/* Total Hires */}
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Total Hires',
            description:
              'Heading for a metric group showing total hires across all recruitment sources for this job opening',
          })}
          icon={<UserRoundCheck className="text-fx-blue size-4" />}
          variant="blue"
          className="shrink-0"
          tooltipTitle={intl.formatMessage({
            defaultMessage: 'How hires is counted?',
            description:
              'Question introducing an explanation of how the total hire count is calculated',
          })}
          tooltipBody={
            goalWindow?.end
              ? intl.formatMessage(
                  {
                    defaultMessage:
                      'Everyone hired from {start} to {end} {timeZone}, across all sources. Includes applicants sourced before the goal started.',
                    description:
                      "Tooltip on an opening's Total Hires card in hiring goal view. {start} and {end} are a date and time such as Aug 24, 2026 9:04am; {timeZone} is the zone's name such as Central Time",
                  },
                  {
                    start: tooltipInstant(goalWindow.start),
                    end: tooltipInstant(goalWindow.end),
                    timeZone: goalWindow.timeZoneName,
                  },
                )
              : goalWindow
                ? intl.formatMessage(
                    {
                      defaultMessage:
                        'Everyone hired since {start} {timeZone}, across all sources. Includes applicants sourced before the goal started.',
                      description:
                        "Tooltip on an opening's Total Hires card in hiring goal view, for a goal with no end date. {start} is a date and time such as Aug 24, 2026 9:04am; {timeZone} is the zone's name such as Central Time",
                    },
                    {
                      start: tooltipInstant(goalWindow.start),
                      timeZone: goalWindow.timeZoneName,
                    },
                  )
                : dateRangeLabel
                  ? intl.formatMessage(
                      {
                        defaultMessage:
                          'Counts everyone hired in the {dateRange} across all sources, including hires from candidates sourced earlier.',
                        description:
                          'Explanation that total hires counts everyone hired in {dateRange} across all sources, including hires from applicants sourced before the period',
                      },
                      { dateRange: dateRangeLabel },
                    )
                  : intl.formatMessage({
                      defaultMessage:
                        'Counts everyone hired across all sources, including hires from candidates sourced earlier.',
                      description:
                        'Explanation that total hires counts everyone hired across all sources, including hires from applicants sourced at any time',
                    })
          }
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Total hires',
              description:
                'Count of all people hired for this job opening across all recruitment sources',
            })}
          >
            <HireStats
              stats={{ actual: hires.actual.total, expected: hires.expected }}
              typographyVariant="large2"
              showOnlyActual={hideExpectedStats || !hires.expected}
              expectedClassName="text-fx-neutral-600"
            />
          </MetricGroupItem>
        </MetricGroup>

        {/* Applicants & Hires */}
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Sourced this period',
            description:
              'Heading for a metric group showing applicants and hires that came from sourcing campaigns in the current period',
          })}
          icon={<UserRoundPlus className="text-fx-neutral-400 size-4" />}
          className="min-w-0 flex-1"
          headerLabel={sourcedHeaderLabel}
          tooltipTitle={intl.formatMessage({
            defaultMessage: 'Sourced this period',
            description:
              'Introduction to an explanation of which applicants and hires are included in the sourced period count',
          })}
          tooltipBody={
            dateRangeLabel
              ? intl.formatMessage(
                  {
                    defaultMessage:
                      'Applicants and hires limited to people you sourced in {dateRange}. This is the population your campaign activity drove.',
                    description:
                      'Explanation that this group counts only applicants and hires from people sourced through campaigns in {dateRange}',
                  },
                  { dateRange: dateRangeLabel },
                )
              : intl.formatMessage({
                  defaultMessage:
                    'Applicants and hires limited to people you sourced in this period. This is the population your campaign activity drove.',
                  description:
                    'Explanation that this group counts only applicants and hires from people sourced through campaigns in this period',
                })
          }
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Applicants',
              description:
                'Number of people who applied through sourcing campaigns in the period, for a single job opening',
            })}
          >
            <HireStats
              stats={applicants}
              typographyVariant="large2"
              showOnlyActual={hideExpectedStats || !applicants.expected}
              expectedClassName="text-fx-neutral-600"
            />
            <ApplicantTargetPopover
              hiringGoal={hiringGoal}
              applicantsStats={applicants}
              conversionStats={conversion}
              isReadOnly={isReadOnly}
            />
          </MetricGroupItem>

          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Hires',
              description:
                'Number of people hired who were originally sourced through campaigns in the period, for a single job opening',
            })}
          >
            <HireStats
              stats={{
                actual: hires.actual.cohort,
                expected: hires.expected,
              }}
              typographyVariant="large2"
              showOnlyActual
            />
          </MetricGroupItem>

          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Conv. To Hire',
              description:
                'Conversion rate from sourced applicant to hire, expressed as a percentage, for a single job opening',
            })}
          >
            <ConversionStats
              stats={conversion}
              typographyVariant="large2"
              showOnlyActual
            />
          </MetricGroupItem>
        </MetricGroup>

        {/* Spend & Cost */}
        <div ref={spendCardRef} className="min-w-0 flex-1">
          <MetricGroup
            label={intl.formatMessage({
              defaultMessage: 'Spend & cost metrics',
              description:
                'Heading for a metric group showing spend, cost per applicant, and cost per hire for sourcing campaigns',
            })}
            icon={<CircleDollarSign className="text-fx-neutral-400 size-4" />}
            className="min-w-0 flex-1"
            isCard
          >
            <MetricGroupItem
              label={intl.formatMessage({
                defaultMessage: 'Spend / Budget',
                description:
                  'Amount spent on sourcing campaigns versus the total allocated budget, for a single job opening',
              })}
            >
              <CurrencyStats
                stats={budget}
                typographyVariant="large2"
                showOnlyActual={hideExpectedStats}
                expectedClassName="text-fx-neutral-600"
              />
              <BudgetTargetPopover
                targetBudget={targetBudget}
                budgetStats={budget}
                spendStats={spendStats}
                automation={opening.automation}
                isReadOnly={isReadOnly}
              />
            </MetricGroupItem>

            <MetricGroupItem
              label={intl.formatMessage({
                defaultMessage: 'Avg. CPA',
                description:
                  'Average cost per applicant across sourcing campaigns for this job opening (CPA = cost per applicant)',
              })}
            >
              <CurrencyStats
                stats={cpa}
                typographyVariant="large2"
                showOnlyActual
              />
            </MetricGroupItem>

            <MetricGroupItem
              label={intl.formatMessage({
                defaultMessage: 'Avg. CPH',
                description:
                  'Average cost per hire across sourcing campaigns for this job opening (CPH = cost per hire)',
              })}
            >
              <CurrencyStats
                stats={{ actual: cph, expected: null }}
                typographyVariant="large2"
                showOnlyActual
              />
            </MetricGroupItem>
          </MetricGroup>
        </div>
      </div>
    </div>
  );
}

export function Analytics() {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<Analytics.Skeleton />}>
        <AnalyticsContent />
      </Suspense>
    </ErrorBoundary>
  );
}

Analytics.Skeleton = function AnalyticsSkeleton() {
  const intl = useIntl();

  return (
    <div className="shrink-0 overflow-x-auto">
      <div className="flex min-w-max flex-row items-stretch gap-4 pb-2">
        {/* Total Hires skeleton */}
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Total Hires',
            description:
              'Heading for a metric group showing total hires across all recruitment sources for this job opening',
          })}
          icon={<UserRoundCheck className="text-fx-blue size-4" />}
          variant="blue"
          className="shrink-0"
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Total hires',
              description:
                'Count of all people hired for this job opening across all recruitment sources',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
        </MetricGroup>

        {/* Applicants & Hires skeleton */}
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Sourced this period',
            description:
              'Heading for a metric group showing applicants and hires that came from sourcing campaigns in the current period',
          })}
          icon={<UserRoundPlus className="text-fx-neutral-400 size-4" />}
          className="min-w-0 flex-1"
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Applicants',
              description:
                'Number of people who applied through sourcing campaigns in the period, for a single job opening',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Hires',
              description:
                'Number of people hired who were originally sourced through campaigns in the period, for a single job opening',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Conv. To Hire',
              description:
                'Conversion rate from sourced applicant to hire, expressed as a percentage, for a single job opening',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
        </MetricGroup>

        {/* Spend & Cost skeleton */}
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Spend & cost metrics',
            description:
              'Heading for a metric group showing spend, cost per applicant, and cost per hire for sourcing campaigns',
          })}
          icon={<CircleDollarSign className="text-fx-neutral-400 size-4" />}
          className="min-w-0 flex-1"
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Spend / Budget',
              description:
                'Amount spent on sourcing campaigns versus the total allocated budget, for a single job opening',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Avg. CPA',
              description:
                'Average cost per applicant across sourcing campaigns for this job opening (CPA = cost per applicant)',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Avg. CPH',
              description:
                'Average cost per hire across sourcing campaigns for this job opening (CPH = cost per hire)',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
        </MetricGroup>
      </div>
    </div>
  );
};
